import SplunkOtelWeb from '@splunk/otel-web'
import checkBrowserType from './checkBrowserType'
import { trace } from '@opentelemetry/api'
import { JWebWindow } from '../interface/JWebWindowInterface'
import { LogLevel } from '@jarvis/jweb-core/dist/plugins/console'
import {
  GetKeyValueOptions,
  ReservedValueStoreKeys,
  ValueStoreGetResult,
  ValueStoreGetSuccessResult,
  ValueStorePlugin
} from '@jarvis/jweb-core'
import { Logger } from '../utils/helperMethods'
import { DeviceSupplier } from '../controller/DeviceProvider/DeviceSupplier'
import { ResourceError } from '../interface/controllerInterface'

const splunkScriptId = 'bootstrap-splunk-rum'
const beaconUrl = 'https://rum-ingest.us1.signalfx.com/v1/rum'
const app = 'bootstrap-controller'
export const outputTracer = trace.getTracer('bootstrapResult')
let splunkRumSessionId: string
export let printerUuid: string
export let printerModelNumber: string
let hpAppVersion: string
let hpAppInstanceId: string

export const injectScript = () => {
  if (document.querySelector(`script[id=${splunkScriptId}]`)) {
    return
  }
  const f = document.getElementsByTagName('script')[0]
  const b = window.document.createElement('script')
  b.async = true
  b.crossOrigin = 'anonymous'
  b.id = splunkScriptId

  const isInternetExplorer = checkBrowserType.isInternetExplorer()

  const splunkFile = isInternetExplorer
    ? 'splunk-otel-web-legacy.js'
    : 'splunk-otel-web.js'

  b.src = `https://cdn.signalfx.com/o11y-gdi-rum/latest/${splunkFile}`
  f.parentNode?.insertBefore(b, f)
}

export function logSplunkSessionIdWhenAvailable() {
  let loggedToBrowser = false
  let loggedToJWeb = false
  const startTime = new Date().getMilliseconds()
  const intervalCheckToLogSplunkId = setInterval(() => {
    try {
      splunkRumSessionId = SplunkOtelWeb.getSessionId()
      const jWeb = (window as JWebWindow)?.JWeb

      if (splunkRumSessionId) {
        const message = `SplunkRum sessionId: ${splunkRumSessionId}`

        if (!loggedToBrowser) {
          loggedToBrowser = true
          console.debug(message)
        }
        if (jWeb && !loggedToJWeb) {
          loggedToJWeb = true
          if (jWeb?.isNative) {
            jWeb?.Plugins?.Console?.log({
              logLevel: LogLevel.Log,
              message,
              tag: '' // according to console.d.ts this is required
            })
          }
        }

        const duration = new Date().getMilliseconds() - startTime
        const timeout = 10 * 1000
        if ((loggedToBrowser && loggedToJWeb) || duration > timeout) {
          clearInterval(intervalCheckToLogSplunkId)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, 500)
}

export const loadValueStoreData = async (valueStore: ValueStorePlugin) => {
  if (valueStore) {
    // Printer UUID and Model Number
    const options: GetKeyValueOptions = {
      keys: [
        ReservedValueStoreKeys.selectedPrinterUuid,
        ReservedValueStoreKeys.selectedPrinterModelNumber
      ]
    }

    const result = await valueStore.get(options)
    ;(result as ValueStoreGetResult).result.forEach((result) => {
      const success = result as ValueStoreGetSuccessResult
      if (success.value) {
        switch (success.key) {
          case ReservedValueStoreKeys.selectedPrinterUuid:
            printerUuid = success.value
            break
          case ReservedValueStoreKeys.selectedPrinterModelNumber:
            printerModelNumber = success.value
            break
        }
      }
    })

    Logger.error(
      'ValueStore loaded values for Printer UUID and Model Number: ',
      printerUuid,
      printerModelNumber
    )
  }
}

export const loadDeviceSupplierData = (deviceSupplier: DeviceSupplier) => {
  const deviceInfo = deviceSupplier.getDeviceInfo()
  hpAppVersion = deviceInfo.appVersion
}

export const setHpAppInstanceId = (appInstanceId: string) => {
  hpAppInstanceId = appInstanceId
}

export const configureSplunkRum = async (
  version: string,
  environment: string,
  rumAuth: string,
  valueStore: ValueStorePlugin,
  deviceSupplier: DeviceSupplier
): Promise<void> => {
  injectScript()
  await loadValueStoreData(valueStore)
  loadDeviceSupplierData(deviceSupplier)
  const interval = setInterval(function () {
    SplunkOtelWeb.init({
      beaconUrl,
      rumAuth,
      app,
      version,
      environment
    })
    clearInterval(interval)
  }, 3000)

  logSplunkSessionIdWhenAvailable()
}

export const resetState = () => {
  splunkRumSessionId = undefined
  printerUuid = undefined
  printerModelNumber = undefined
}

/**
 * Triggers an custom event on Splunk RUM.
 * @param workflowName the string that will identify the event on Splunk RUM.
 */
export const triggerSplunkCustomEvent = async (
  workflowName: string,
  resourceError?: ResourceError
) => {
  const span = outputTracer.startSpan(workflowName, {
    attributes: {
      'workflow.name': workflowName,
      'printer.uuid': printerUuid,
      'printer.modelNumber': printerModelNumber
    }
  })
  if (splunkRumSessionId) {
    span.setAttribute('sessionId', splunkRumSessionId)
  }
  if (hpAppVersion) {
    span.setAttribute('hpApp.version', hpAppVersion)
  }
  if (hpAppInstanceId) {
    span.setAttribute('hpApp.instanceId', hpAppInstanceId)
  }
  if (
    resourceError &&
    resourceError.executionErrors &&
    resourceError.executionErrors.length > 0
  ) {
    resourceError.executionErrors.forEach((error) => {
      span.setAttribute(error.gun, error.errorDetail)
    })
  }
  span.end()
}
